import {
  Avatar,
  Badge,
  Box,
  Button,
  COLOR_PALETTE,
  Group,
  Icon,
  Modal,
  Popover,
  Progress,
  Stack,
  Text,
  TextInput,
  useShowNotification,
} from '@inflearn/ds-react';
import { faCheck, faXmark } from '@inflearn/pro-solid-svg-icons';
import { Trans, useTranslation } from 'next-i18next';
import qs from 'query-string';
import { type FormEventHandler, useEffect, useState } from 'react';
import { ResponseStatus } from '../../../../resources/@types/accountSchema';
import { ApiError } from '../../../../resources/ApiError';
import { useAccountsSlugConfirmUpdateMutation } from '../../../mutations/useAccountsSlugConfirmUpdateMutation';
import {
  HAS_VALID_SLUG_FORMAT,
  SLUG_MAX_LENGTH,
  SLUG_MIN_LENGTH,
} from '../../../validators/constants';

// 유효성 검사 타입 정의
type ValidationState = {
  lengthValid: boolean;
  charsValid: boolean;
  specialCharsInvalid: boolean;
  apiValid: boolean;
};

// 유효성 검사 아이템 컴포넌트
type ValidationItemProps = {
  isValid: boolean;
  text: string;
};

const ValidationItem = ({ isValid, text }: ValidationItemProps) => {
  return (
    <Group spacing={8}>
      <Icon
        width={12.5}
        size={'2xs'}
        icon={isValid ? faCheck : faXmark}
        color={isValid ? 'infgreen' : 'red'}
      ></Icon>
      <Text color={isValid ? 'infgreen' : 'red'} weight={400} size="xs" component={'span'}>
        {text}
      </Text>
    </Group>
  );
};

export const UserSlugModal = ({
  initialSlug,
  isOpened,
  onClose,
  nickname,
  thumbnailUrl,
  userId,
}: {
  initialSlug: string;
  isOpened: boolean;
  onClose: () => void;
  nickname: string;
  thumbnailUrl: string;
  userId: number;
}) => {
  const { t } = useTranslation();
  const { showNotification } = useShowNotification();

  const [slug, setSlug] = useState(initialSlug);
  const [isFocused, setIsFocused] = useState(false);
  const [validation, setValidation] = useState<ValidationState>({
    lengthValid: false,
    charsValid: false,
    specialCharsInvalid: false,
    apiValid: true,
  });
  const { mutate } = useAccountsSlugConfirmUpdateMutation({
    id: userId,
    options: {
      onSuccess: () => {
        showNotification({
          type: 'success',
          title: t('프로필 주소 설정 완료'),
        });

        onClose();
      },
      onError: (error) => {
        if (error instanceof ApiError && error.statusCode === ResponseStatus.CONFLICT) {
          setValidation((prev) => ({ ...prev, apiValid: false }));

          showNotification({
            type: 'error',
            title: t('이미 사용중인 주소입니다.'),
          });
          return;
        }

        // TODO 실패 피드백 추가
        showNotification({
          type: 'error',
          title: t('프로필 주소 설정 실패'),
          content: t('문제가 지속되면 지원팀에게 문의해 주세요.'),
        });
      },
    },
  });
  const isFormValid =
    validation.lengthValid &&
    validation.charsValid &&
    validation.specialCharsInvalid &&
    validation.apiValid;
  const [progressValue, setProgressValue] = useState(100);

  // 유효성 검사 항목 정의
  const validationItems = [
    {
      isValid: validation.lengthValid,
      text: t('1자 이상 20자 이하로 입력해주세요.'),
    },
    {
      isValid: validation.charsValid,
      text: t('영문 소문자와 숫자로만 입력해주세요.'),
    },
    {
      isValid: validation.specialCharsInvalid,
      text: t('대문자, 공백, 특수문자, 이모지는 사용할 수 없어요.'),
    },
  ];

  // 유효성 검사 함수
  const validateSlug = (value: string) => {
    const lengthValid = value.length >= SLUG_MIN_LENGTH && value.length <= SLUG_MAX_LENGTH;
    const charsValid = HAS_VALID_SLUG_FORMAT.test(value);

    const newValidation = {
      lengthValid,
      charsValid,
      specialCharsInvalid: charsValid,
      apiValid: true,
    };
    setValidation(newValidation);

    const validConditionsCount = Object.values(newValidation).filter(Boolean).length;
    const totalConditions = Object.keys(newValidation).length;
    const newProgressValue = Math.round((validConditionsCount / totalConditions) * 100);
    setProgressValue(newProgressValue);

    return lengthValid && charsValid;
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSlug(newValue);
    validateSlug(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    mutate({ slug });
  };

  useEffect(() => {
    validateSlug(initialSlug);
  }, []);

  return (
    <Modal.Root opened={isOpened} onClose={onClose} size="sm">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{t('프로필 주소 설정')}</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Stack pb={24} spacing={16} align="center">
              <Stack spacing={8} align="center">
                <Avatar
                  src={qs.stringifyUrl({
                    url: thumbnailUrl,
                    query: { w: 54 * 2, f: 'avif' },
                  })}
                  alt="avatar"
                  size={'lg'}
                  radius={'xl'}
                />

                <Stack spacing={0} w="100%" align="center">
                  <Text size={'lg'} weight={700}>
                    {nickname}
                  </Text>
                  <Badge variant="light" color="gray" size="md" radius="xl">
                    @{slug}
                  </Badge>
                </Stack>
              </Stack>
              <Popover
                withinPortal
                opened={isFocused}
                position="bottom"
                width="target"
                withArrow
                trapFocus={false}
                closeOnEscape={false}
                closeOnClickOutside={false}
                shadow="md"
              >
                <Popover.Target>
                  <div style={{ width: '100%' }}>
                    <TextInput
                      component={'div'}
                      value={slug}
                      onChange={handleSlugChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      error={!isFormValid}
                      styles={{
                        input: { display: 'flex', alignItems: 'center' },
                      }}
                    >
                      <label
                        htmlFor={'input'}
                        css={{ color: COLOR_PALETTE.gray[6], flexShrink: 0 }}
                      >
                        inflearn.com/user/@
                      </label>
                      <TextInput
                        value={slug}
                        id={'input'}
                        variant={'unstyled'}
                        {...(!isFormValid && { 'data-autofocus': true })}
                      />
                    </TextInput>
                  </div>
                </Popover.Target>
                <Popover.Dropdown py={12} px={16}>
                  <Stack spacing={8}>
                    <Progress
                      value={progressValue}
                      color={isFormValid ? 'infgreen' : 'red'}
                      size="sm"
                      radius="xl"
                      style={{ height: '4px' }}
                    />
                    <Stack spacing={4}>
                      {validationItems.map((item, index) => (
                        <ValidationItem key={index} isValid={item.isValid} text={item.text} />
                      ))}
                    </Stack>
                  </Stack>
                </Popover.Dropdown>
              </Popover>

              <Box w={'100%'}>
                <Text weight={400} size={'sm'}>
                  • {t('내 프로필 주소를 설정해 주세요!')}
                </Text>
                <Text weight={400} size={'sm'}>
                  •{' '}
                  <Trans
                    i18nKey="입력하신 값은 글로벌 닉네임으로도 사용돼요."
                    components={{
                      highlight: <Text component={'span'} color={'infgreen.7'} />,
                    }}
                  />
                </Text>
                <Text weight={400} size={'sm'}>
                  • {t('입력한 주소는 프로필 설정에서 추후 변경할 수 있습니다.')}
                </Text>
              </Box>
            </Stack>
          </Modal.Body>
          <Modal.Footer align="right">
            <Button variant={'default'} onClick={onClose}>
              {t('취소')}
            </Button>
            <Button
              color={'infgreen'}
              size="md"
              type={'submit'}
              disabled={!isFormValid}
              {...(isFormValid && { 'data-autofocus': true })}
            >
              {t('설정 완료')}
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};
